import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  // Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  // IconButton,
  // InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Transition } from '../../components/Transition';
import { LANGUAGE, SLIDER_ACTION_TYPE } from '../../constants';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import { getAssetURl } from '../../utils/url';
import { uploadAssets } from '../../utils/uploadAssets';
import { useAppDispatch, useAppSelector } from '../../store';
import { ActionType, DocumentType } from '../../enum';
import { uniqBy } from 'lodash';
import { useEffect } from 'react';
import { useRemountFileInput } from '../../hooks/useRemountFileInput';
import {
  findSelectCategory,
  searchSelectCategory,
} from '../../store/selectCategory/api';
import {
  reset as resetCategory,
  setSkip as setSkipCategory,
} from '../../store/selectCategory/selectCategorySlice';
import AutoComplete from '../../components/AutoComplete';
import {
  findSelectCategoryCake,
  searchSelectCategoryCake,
} from '../../store/selectCategoryCake/api';
import {
  reset as resetCategoryCake,
  setSkip as setSkipCategoryCake,
} from '../../store/selectCategoryCake/selectCategoryCakeSlice';
import {
  bannerPlacementFlags,
  getBannerPlacement,
} from '../../store/slider/api/interface';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (key: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
}

const SliderFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
}) => {
  const { accessToken } = useAppSelector((state) => state.auth);
  const { fileInputRef, fileInputKey } = useRemountFileInput(
    toggle,
    values?.id,
    Boolean(values?.imageUrl),
    'imageUrl'
  );
  const dispatch = useAppDispatch();
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  // Category Data
  const {
    data: categoryData,
    isLoading: isCategoryLoading,
    skip: categorySkip,
    hasMore: categoryHasMore,
    status: categoryStatus,
  } = useAppSelector((state) => state.selectCategory);

  useEffect(() => {
    if (categoryStatus === 'idle') {
      dispatch(
        findSelectCategory({
          limit: 10,
          skip: categorySkip,
        })
      );
    }
  }, [categorySkip, categoryStatus]);

  const onScrollFetchCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      categoryHasMore
    ) {
      dispatch(setSkipCategory({ skip: categorySkip + 10 }));
      dispatch(
        findSelectCategory({
          limit: 10,
          skip: categorySkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetCategory());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipCategory({ skip: 0 }));
    dispatch(searchSelectCategory(params));
  };

  const uniqueCategoryData = uniqBy(
    [
      // ...(response?.category ? [response?.category] : []),
      ...categoryData,
    ],
    'id'
  );

  // Category Cake Data
  const {
    data: categoryCakeData,
    isLoading: isCategoryCakeLoading,
    skip: categoryCakeSkip,
    hasMore: categoryCakeHasMore,
    status: categoryCakeStatus,
  } = useAppSelector((state) => state.selectCategoryCake);

  useEffect(() => {
    if (categoryCakeStatus === 'idle') {
      dispatch(
        findSelectCategoryCake({
          limit: 10,
          skip: categoryCakeSkip,
        })
      );
    }
  }, [categoryCakeSkip, categoryCakeStatus]);

  const onScrollFetchCategoryCake = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      categoryCakeHasMore
    ) {
      dispatch(setSkipCategoryCake({ skip: categoryCakeSkip + 10 }));
      dispatch(
        findSelectCategoryCake({
          limit: 10,
          skip: categoryCakeSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchCategoryCake = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetCategoryCake());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipCategoryCake({ skip: 0 }));
    dispatch(searchSelectCategoryCake(params));
  };

  const uniqueCategoryCakeData = uniqBy(
    [
      // ...(response?.category ? [response?.category] : []),
      ...categoryCakeData,
    ],
    'id'
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  return (
    <Dialog
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>Slider & Banner</DialogTitle>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleSubmit();
        }}
        autoComplete='off'
      >
        <DialogContent dividers>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                fullWidth
                label={'Position'}
                name='position'
                id='position'
                onChange={handleChange}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                }}
                value={values.position}
                error={Boolean(errors.position && touched.position)}
                helperText={
                  Boolean(errors.position && touched.position) &&
                  errors?.position
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <FormControl
                fullWidth
                error={Boolean(touched?.actionType && errors?.actionType)}
              >
                <InputLabel>{'Type'}</InputLabel>
                <Select
                  name='actionType'
                  labelId='actionType'
                  id='actionType'
                  value={values.actionType || ''}
                  label={'Type'}
                  onChange={(e) => setFieldValue('actionType', e.target.value)}
                  MenuProps={{
                    style: {
                      maxHeight: 400,
                    },
                  }}
                >
                  {SLIDER_ACTION_TYPE?.map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(touched.actionType && errors.actionType) && (
                  <FormHelperText>{errors?.actionType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {values?.actionType === ActionType.occasionCategory && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                <AutoComplete
                  key={`o-${values?.actionId}`}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('actionId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('actionId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(touched.actionId && errors.actionId)}
                  helperText={
                    Boolean(touched.actionId && errors.actionId)
                      ? errors.actionId
                      : ''
                  }
                  fetchFunction={onScrollFetchCategory}
                  label={'Select Occasion'}
                  loading={isCategoryLoading}
                  options={uniqueCategoryData}
                  searchFunction={searchCategory}
                  value={
                    uniqueCategoryData?.filter(
                      (category) => category.id === values.actionId
                    )?.[0] || null
                  }
                />
              </Grid>
            )}
            {values?.actionType === ActionType.occasionCake && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                <AutoComplete
                  key={`oC-${values?.actionId}`}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('actionId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('actionId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(touched.actionId && errors.actionId)}
                  helperText={
                    Boolean(touched.actionId && errors.actionId)
                      ? errors.actionId
                      : ''
                  }
                  fetchFunction={onScrollFetchCategoryCake}
                  label={'Select Occasion Cake'}
                  loading={isCategoryCakeLoading}
                  options={uniqueCategoryCakeData}
                  searchFunction={searchCategoryCake}
                  value={
                    uniqueCategoryCakeData?.filter(
                      (category) => category.id === values.actionId
                    )?.[0] || null
                  }
                />
              </Grid>
            )}
            <Grid
              item
              xs={values?.imageUrl ? 8.5 : 12}
              sm={values?.imageUrl ? 8.5 : 12}
            >
              <TextField
                key={fileInputKey}
                type='file'
                fullWidth
                name='imageUrl'
                id='imageUrl'
                inputRef={fileInputRef}
                onChange={(e) =>
                  handleFileChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    DocumentType.Slider,
                    'imageUrl'
                  )
                }
                inputProps={{ multiple: false, accept: 'image/*' }}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                label={'Image URL'}
                error={Boolean(errors.imageUrl && touched.imageUrl)}
                helperText={
                  Boolean(errors.imageUrl && touched.imageUrl) &&
                  errors.imageUrl
                }
              />
            </Grid>
            {values?.imageUrl && (
              <Grid
                item
                xs={3.5}
                sm={3.5}
              >
                <Avatar
                  imgProps={{ crossOrigin: 'anonymous' }}
                  src={getAssetURl(values?.imageUrl, assetUrl)}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    width: { xs: '75px', sm: '100px' },
                    height: 50,
                  }}
                />
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={12}
            >
              <Typography variant='body2'>
                {'Minimum 664 px or higher X 490px'}
              </Typography>
              <Typography
                variant='caption'
                component='div'
                color='text.secondary'
              >
                {
                  'We have limited banner height to maintain UI. We had to crop from both left & right side in view for different devices to make it responsive. Before designing banner keep these points in mind.'
                }
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <FormControl
                fullWidth
                error={Boolean(touched?.language && errors?.language)}
              >
                <InputLabel>{'Language'}</InputLabel>
                <Select
                  name='language'
                  labelId='language'
                  id='language'
                  value={values.language}
                  label={'Language'}
                  onChange={(e) => setFieldValue('language', e.target.value)}
                  MenuProps={{
                    style: {
                      maxHeight: 400,
                    },
                  }}
                >
                  {LANGUAGE?.map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(touched.language && errors.language) && (
                  <FormHelperText>{errors?.language}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {/* <Grid
              item
              xs={12}
              sm={12}
            >
               <TextField
                id='value'
                name='value'
                label='Value'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.value}
                error={Boolean(errors.value && touched.value)}
                helperText={
                  Boolean(errors.value && touched.value) && errors.value
                }
              /> 
            </Grid> */}
            <Grid
              item
              xs={12}
            >
              <FormControl fullWidth>
                <InputLabel id='banner-placement-label'>Banner type</InputLabel>
                <Select
                  labelId='banner-placement-label'
                  id='bannerType'
                  name='bannerType'
                  label='Banner type'
                  value={getBannerPlacement(values)}
                  onChange={(e) => {
                    const flags = bannerPlacementFlags(
                      e.target.value as ReturnType<typeof getBannerPlacement>
                    );
                    Object.entries(flags).forEach(([key, flagValue]) => {
                      setFieldValue(key, flagValue);
                    });
                  }}
                >
                  <MenuItem value=''>Slider</MenuItem>
                  <MenuItem value='advertise'>Advertise Banner</MenuItem>
                  <MenuItem value='homeBottom'>Home page bottom image</MenuItem>
                  <MenuItem value='cake'>Cake</MenuItem>
                  <MenuItem value='flower'>Flower</MenuItem>
                  <MenuItem value='inviteEarn'>Invite & Earn</MenuItem>
                  <MenuItem value='eventService'>Event Service</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    name='activeState'
                    onChange={(e) =>
                      setFieldValue('activeState', e.target.checked)
                    }
                    checked={values.activeState}
                  />
                }
                label={'Active Status'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            type='button'
            onClick={() => {
              resetForm();
              onClose();
            }}
            variant='outlined'
          >
            {'Cancel'}
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
          >
            {'Save'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SliderFormModal;
